import { Component } from '@theme/component';
import { ThemeEvents, CartUpdateEvent } from '@theme/events';

/**
 * A custom element that displays a cart icon.
 * Shows empty (0) or one of eleven TDS cart SVGs for counts 1–10 and 10+.
 *
 * @typedef {object} Refs
 * @property {HTMLElement} cartIconState - The cart icon state wrapper.
 * @property {HTMLElement} cartIconMask - Mask element showing the cart silhouette in currentColor.
 *
 * @extends {Component<Refs>}
 */
class CartIcon extends Component {
  requiredRefs = ['cartIconState', 'cartIconMask'];

  connectedCallback() {
    super.connectedCallback();

    document.addEventListener(ThemeEvents.cartUpdate, this.onCartUpdate);
    window.addEventListener('pageshow', this.onPageShow);
    this.ensureCartIconIsCorrect();
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    document.removeEventListener(ThemeEvents.cartUpdate, this.onCartUpdate);
    window.removeEventListener('pageshow', this.onPageShow);
  }

  /**
   * Handles the page show event when the page is restored from cache.
   * @param {PageTransitionEvent} event - The page show event.
   */
  onPageShow = (event) => {
    if (event.persisted) {
      this.ensureCartIconIsCorrect();
    }
  };

  /**
   * Handles the cart update event.
   * @param {CartUpdateEvent} event - The cart update event.
   */
  onCartUpdate = (event) => {
    const itemCount = event.detail.data?.itemCount ?? 0;
    const comingFromProductForm = event.detail.data?.source === 'product-form-component';

    this.updateCartIcon(itemCount, comingFromProductForm);
  };

  /**
   * Updates the cart icon based on item count.
   * @param {number} itemCount - The number of items (or items added when coming from product form).
   * @param {boolean} comingFromProductForm - Whether the cart update is coming from the product form.
   */
  updateCartIcon = (itemCount, comingFromProductForm) => {
    let totalCount = itemCount;
    if (comingFromProductForm) {
      const stored = sessionStorage.getItem('cart-count');
      if (stored) {
        try {
          const { value } = JSON.parse(stored);
          totalCount = parseInt(value, 10) + itemCount;
        } catch (_) {
          // use itemCount as-is
        }
      }
    }
    const safeCount = Number.parseInt(totalCount, 10);
    const count = Number.isFinite(safeCount) ? Math.max(0, safeCount) : 0;
    const hasItems = count > 0;
    const iconIndex = count <= 0 ? 0 : count >= 11 ? 11 : count;
    const url = this.getCartIconAssetUrl(iconIndex);
    const mask = `url('${url}')`;
    this.refs.cartIconMask.style.setProperty('-webkit-mask-image', mask);
    this.refs.cartIconMask.style.setProperty('mask-image', mask);
    this.classList.toggle('header-actions__cart-icon--has-cart', hasItems);

    sessionStorage.setItem(
      'cart-count',
      JSON.stringify({
        value: String(count),
        timestamp: Date.now(),
      })
    );
  };

  /**
   * Returns asset url for the current cart icon state.
   * @param {number} iconIndex - 0 = empty, 1–10 = count, 11 = ten-plus.
   * @returns {string}
   */
  getCartIconAssetUrl = (iconIndex) => {
    const stateKeys = [
      'cartIconZero',
      'cartIconOne',
      'cartIconTwo',
      'cartIconThree',
      'cartIconFour',
      'cartIconFive',
      'cartIconSix',
      'cartIconSeven',
      'cartIconEight',
      'cartIconNine',
      'cartIconTen',
      'cartIconTenPlus',
    ];
    return this.dataset[stateKeys[iconIndex]] || this.dataset.cartIconZero || '';
  };

  /**
   * Ensures the cart icon state is correct when page is restored from cache.
   */
  ensureCartIconIsCorrect = () => {
    const sessionStorageCount = sessionStorage.getItem('cart-count');

    if (sessionStorageCount === null) return;

    try {
      const { value, timestamp } = JSON.parse(sessionStorageCount);

      if (Date.now() - timestamp < 10000) {
        const count = parseInt(value, 10);

        if (count >= 0) {
          this.updateCartIcon(count, false);
        }
      }
    } catch (_) {
      // no-op
    }
  };
}

if (!customElements.get('cart-icon')) {
  customElements.define('cart-icon', CartIcon);
}
